import type { ApiKey } from '@servicetim/shared';
import { useApiKeys, useRevokeApiKey } from '@servicetim/ui/api';
import {
  Badge,
  Button,
  type ColumnDef,
  DataTable,
  DropdownMenuItem,
  Label,
  pagedTableProps,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import { Ban, KeyRound, Plus } from 'lucide-react';
import { useCallback, useId, useMemo, useState } from 'react';
import { formatDateTime } from '../common/format';
import { RowActions } from '../common/row-actions';
import { withProblemMessage } from '../common/server-errors';
import { CreateApiKeyDialog } from './create-api-key-dialog';

const SHOWN_SCOPES = 3;

function keyStatus(key: ApiKey): { label: string; variant: 'success' | 'destructive' | 'warning' } {
  if (key.revokedAt) return { label: 'revoked', variant: 'destructive' };
  if (key.expiresAt && new Date(key.expiresAt).getTime() <= Date.now()) {
    return { label: 'expired', variant: 'warning' };
  }
  return { label: 'active', variant: 'success' };
}

export function ApiKeysTable() {
  const id = useId();
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const keys = useApiKeys({ includeRevoked });
  const revoke = useRevokeApiKey();
  const confirm = useConfirm();
  const [creating, setCreating] = useState(false);

  const onRevoke = useCallback(
    (key: ApiKey) =>
      void confirm({
        title: `Revoke ${key.name}?`,
        description:
          'Requests with this key fail immediately and its service user is disabled. This cannot be undone; create a new key instead.',
        confirmLabel: 'Revoke',
        destructive: true,
        onConfirm: async () => {
          await withProblemMessage(() => revoke.mutateAsync(key.id));
          toast.success(`${key.name} revoked`);
        },
      }),
    [confirm, revoke.mutateAsync],
  );

  const columns = useMemo<ColumnDef<ApiKey, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span className="flex items-center gap-2 font-medium text-fg">
            <KeyRound className="size-4 text-fg-subtle" aria-hidden="true" />
            {row.original.name}
          </span>
        ),
      },
      {
        accessorKey: 'keyPrefix',
        header: 'Prefix',
        cell: ({ row }) => (
          <code className="rounded bg-surface-sunken px-1 font-mono text-xs">
            stk_…_{row.original.keyPrefix}…
          </code>
        ),
      },
      {
        id: 'scopes',
        header: 'Scopes',
        enableSorting: false,
        cell: ({ row }) => {
          const scopes = row.original.scopes;
          const hidden = scopes.slice(SHOWN_SCOPES);
          return (
            <span className="flex flex-wrap gap-1">
              {scopes.slice(0, SHOWN_SCOPES).map((scope) => (
                <Badge key={scope} variant={scope === 'admin' ? 'info' : 'secondary'}>
                  {scope}
                </Badge>
              ))}
              {hidden.length > 0 ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" tabIndex={0}>
                      +{hidden.length}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>{hidden.join(', ')}</TooltipContent>
                </Tooltip>
              ) : null}
            </span>
          );
        },
      },
      {
        id: 'projects',
        header: 'Projects',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.projectIds ? (
            <span>{row.original.projectIds.length} restricted</span>
          ) : (
            <span className="text-fg-muted">All</span>
          ),
      },
      {
        accessorKey: 'lastUsedAt',
        header: 'Last used',
        cell: ({ row }) => (
          <span className="text-fg-muted">{formatDateTime(row.original.lastUsedAt)}</span>
        ),
      },
      {
        accessorKey: 'expiresAt',
        header: 'Expires',
        cell: ({ row }) => (
          <span className="text-fg-muted">
            {row.original.expiresAt ? formatDateTime(row.original.expiresAt) : 'never'}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const status = keyStatus(row.original);
          return <Badge variant={status.variant}>{status.label}</Badge>;
        },
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        enableSorting: false,
        size: 48,
        cell: ({ row }) => (
          <RowActions label={`Actions for ${row.original.name}`}>
            <DropdownMenuItem
              variant="destructive"
              disabled={row.original.revokedAt !== null}
              onSelect={() => onRevoke(row.original)}
            >
              <Ban /> Revoke
            </DropdownMenuItem>
          </RowActions>
        ),
      },
    ],
    [onRevoke],
  );

  return (
    <>
      <DataTable
        columns={columns}
        {...pagedTableProps(keys)}
        getRowId={(k) => k.id}
        caption="API keys"
        toolbar={
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                id={`${id}-revoked`}
                checked={includeRevoked}
                onCheckedChange={setIncludeRevoked}
              />
              <Label htmlFor={`${id}-revoked`}>Show revoked</Label>
            </div>
            <Button className="ml-auto" onClick={() => setCreating(true)}>
              <Plus /> New API key
            </Button>
          </div>
        }
        emptyTitle="No API keys"
        emptyDescription="Integrations authenticate with a scoped key instead of a user session."
        emptyAction={
          <Button onClick={() => setCreating(true)}>
            <Plus /> New API key
          </Button>
        }
      />
      {creating ? <CreateApiKeyDialog onClose={() => setCreating(false)} /> : null}
    </>
  );
}
